import type {
  CareProfileAnswer,
  ExtractedFact,
  FactVersion,
  ReviewFact,
} from '@karehero/models';

import {
  askedSteps,
  BENEFITS_CALCULATOR_STEPS,
  CARE_RECIPIENT_PLACEHOLDER,
  type BenefitsCalculatorAnswers,
  type BenefitsCalculatorStep,
} from 'config/benefitsCalculatorV2Steps';
import { parseAnswerValue } from 'utils/careProfileAnswers';
import { optionIDsOf } from 'utils/extractedFacts';

/** A prediction of a question's answer: the fact, and the options it picks. */
export interface Prediction {
  factId: string;
  selected: string[];
}

/** A calculator question we already have an answer, or predictions, for. */
export interface KnownAnswer {
  /** The care profile question, and the row's id. */
  questionId: string;
  row: ReviewFact;
  /** The unconfirmed facts predicting the answer, which saving it settles. */
  predictions: Prediction[];
}

/** Answers nothing; never shown as a known answer. */
const NOT_SURE = 'not-sure';

/** Every question the calculator can ask, to fetch predictions for. */
export const BENEFITS_CALCULATOR_QUESTION_IDS = BENEFITS_CALCULATOR_STEPS.map(
  (step) => step.id,
);

const keyOf = (selected: string[]) => [...selected].sort().join(',');

const sourceOf = (fact: ExtractedFact) =>
  fact.documentId ? 'Read from a document' : 'From what you told us';

interface Known {
  current: string[];
  predictions: Prediction[];
  /** One fact per distinct prediction, for its source. */
  distinct: { selected: string[]; fact: ExtractedFact }[];
}

const knownByQuestion = (
  facts: ExtractedFact[],
  answers: CareProfileAnswer[],
) => {
  const known: Record<string, Known> = {};
  const entryOf = (questionId: string) =>
    (known[questionId] ??= { current: [], predictions: [], distinct: [] });

  for (const answer of answers) {
    entryOf(answer.careProfileQuestionId).current = parseAnswerValue(
      answer.value,
    );
  }

  for (const fact of facts) {
    const selected = optionIDsOf(fact);
    if (fact.confirmedAt || fact.dismissedAt || !selected.length) continue;
    for (const { questionId } of fact.mappings) {
      const entry = entryOf(questionId);
      entry.predictions.push({ factId: fact.id, selected });
      if (!entry.distinct.some((d) => keyOf(d.selected) === keyOf(selected)))
        entry.distinct.push({ selected, fact });
    }
  }
  return known;
};

/** Each one's answer when it is settled enough to decide what else is asked. */
const settledAnswers = (known: Record<string, Known>) => {
  const answers: BenefitsCalculatorAnswers = {};
  for (const [questionId, entry] of Object.entries(known)) {
    if (entry.current.length) answers[questionId] = entry.current;
    else if (entry.distinct.length === 1)
      answers[questionId] = entry.distinct[0].selected;
  }
  return answers;
};

const toRow = (
  step: BenefitsCalculatorStep,
  entry: Known,
  careRecipientName: string,
): ReviewFact | undefined => {
  const choices = (step.options ?? [])
    .filter((option) => option.id !== NOT_SURE)
    .map((option) => ({ value: option.id, label: option.label }));
  const offered = (selected: string[]) =>
    selected.filter((value) => choices.some((c) => c.value === value));
  const labelOf = (selected: string[]) =>
    choices
      .filter((choice) => selected.includes(choice.value))
      .map((choice) => choice.label)
      .join(', ');

  const base = {
    id: step.id,
    label: step.ask.replaceAll(CARE_RECIPIENT_PLACEHOLDER, careRecipientName),
    control: step.isMultiSelect ? ('choices' as const) : ('choice' as const),
    choices,
  };

  const current = offered(entry.current);
  if (current.length)
    return {
      ...base,
      status: 'confirmed',
      value: labelOf(current),
      selected: current,
    };

  const versions: FactVersion[] = entry.distinct
    .map(({ selected, fact }) => ({
      selected: offered(selected),
      fact,
    }))
    .filter(({ selected }) => selected.length)
    .map(({ selected, fact }) => ({
      value: labelOf(selected),
      source: sourceOf(fact),
      selected,
    }));

  if (versions.length === 1)
    return {
      ...base,
      status: 'inferred',
      value: versions[0].value,
      selected: versions[0].selected,
    };
  if (versions.length > 1) return { ...base, status: 'conflict', versions };
  return undefined;
};

/**
 * The calculator's questions we already have an answer to, from the care
 * profile, or a prediction of one, from extracted facts, in the order they're
 * asked. Only the questions the known answers call for are included.
 */
export const benefitsCalculatorKnownAnswers = (
  facts: ExtractedFact[],
  answers: CareProfileAnswer[],
  careRecipientName: string,
): KnownAnswer[] => {
  const known = knownByQuestion(facts, answers);

  return askedSteps(settledAnswers(known)).flatMap((step) => {
    const entry = known[step.id];
    const row = entry && toRow(step, entry, careRecipientName);
    return row
      ? [{ questionId: step.id, row, predictions: entry.predictions }]
      : [];
  });
};
